import React from 'react';
import { useNavigate } from 'react-router-dom';
import Typography from '@material-ui/core/Typography';
import { isMobileScreen } from '@apply/ui';
import { Button, ButtonTheme, Panel, PanelBorderRadius, PanelTheme, EmailBox, PasswordBox } from '@apply/components';
import { LogoLink } from '../common_v2/components/LogoLink';

export type LoginFormProps = {
    email: string;
    onEmailChange: (value: string) => void;
    onEmailValidation: (valid: boolean) => void;
    password: string;
    onPasswordChange: (value: string) => void;
    onPasswordValidation: (valid: boolean) => void;
    onSubmit: () => void;
    canSubmit: boolean;
    submitting: boolean;
    error: string | null;
    attempts: number;
};

const FormBody: React.FC<LoginFormProps> = ({
    email,
    onEmailChange,
    onEmailValidation,
    password,
    onPasswordChange,
    onPasswordValidation,
    onSubmit,
    canSubmit,
    submitting,
    error,
    attempts,
}) => {
    const navigate = useNavigate();

    const onEnterKey = () => {
        if (email && password && !submitting) {
            onSubmit();
        }
    };

    return (
        <>
            <div className={`preAuthenticationLogoLink ${isMobileScreen() ? 'mobile' : ''}`}>
                <LogoLink />
            </div>
            <main>
                <h2>Apply Log In</h2>
                <br />
                <div className='login_register-fields'>
                    <EmailBox
                        id='emailAddress'
                        data-testid='login-page-email-address'
                        name='emailAddress'
                        value={email}
                        onChange={onEmailChange}
                        onEnterKey={onEnterKey}
                        onValidationEvent={onEmailValidation}
                        label='Email Address'
                        required={true}
                    />
                    <PasswordBox
                        id='password'
                        data-testid='login-page-password'
                        name='password'
                        value={password}
                        onChange={onPasswordChange}
                        onEnterKey={onEnterKey}
                        onValidationEvent={onPasswordValidation}
                        label='Password'
                        required={true}
                    />
                </div>
                {error && (
                    <div>
                        <Typography style={{ color: 'red' }}>{error}</Typography>
                    </div>
                )}
                {attempts > 3 && (
                    <div
                        data-testid='login-page-max-attempts-message'
                        className='login maxAttemptsExceeded'
                    >
                        <span style={{ paddingTop: '16px' }}>Would you like to</span>
                        <Button
                            id='attempt-exceeded-passwordreset-button'
                            key='attempt-exceeded-passwordreset-button'
                            theme={ButtonTheme.LinkBlue}
                            handleClick={() => navigate('/forgotpassword')}
                            width='165px'
                        >
                            Reset your password?
                        </Button>
                    </div>
                )}
                <div className='login primaryActions'>
                    <Button
                        id='primaryButton'
                        theme={ButtonTheme.Primary}
                        width='80%'
                        disabled={!canSubmit}
                        handleClick={onSubmit}
                    >
                        Sign In
                    </Button>
                </div>
                <div className='login secondaryActions'>
                    <div style={{ width: '100%' }}>
                        <Button
                            id='register-button'
                            key='register-button'
                            theme={ButtonTheme.LinkBlue}
                            handleClick={() => navigate('/register')}
                            width='100px'
                        >
                            Register
                        </Button>
                    </div>
                    <div style={{ float: 'right', width: 'auto' }}>
                        <Button
                            id='forgot-password-button'
                            key='forgot-password-button'
                            theme={ButtonTheme.LinkBlue}
                            handleClick={() => navigate(`/forgotPassword?email=${encodeURIComponent(email)}`)}
                            width='100px'
                        >
                            Can't log in?
                        </Button>
                    </div>
                </div>
            </main>
        </>
    );
};

export const LoginForm: React.FC<LoginFormProps> = (props) => {
    if (isMobileScreen()) {
        return (
            <div className='authenticationContainer mobile'>
                <FormBody {...props} />
            </div>
        );
    }
    return (
        <div className='authenticationContainer-desktop'>
            <Panel
                theme={PanelTheme.White}
                borderRadius={PanelBorderRadius.Medium}
                width={'90%'}
            >
                <div className='authenticationContainer-desktop-desktopWrapper'>
                    <FormBody {...props} />
                </div>
            </Panel>
        </div>
    );
};
